import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Package,
  Loader2,
  Box,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Tags,
  BarChart3,
  Clock,
  RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function InventarioDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventario/dashboard`, getAuthHeaders());
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching inventory stats:", err);
      toast.error("Error al cargar estadísticas de inventario");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: "DOP"
    }).format(value || 0).replace("DOP", "RD$");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="inventario-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Inventario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Dashboard de control de inventario
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="kpi-carousel grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/inventario/articulos")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Artículos</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats?.total_articulos || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Box className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/inventario/categorias")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Categorías</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats?.total_categorias || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Tags className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-slate-200 hover:shadow-md transition-shadow cursor-pointer ${stats?.articulos_stock_bajo > 0 ? "border-red-200 bg-red-50/50" : ""}`} onClick={() => navigate("/inventario/articulos?stock_bajo=true")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Stock Bajo</p>
                  <p className="text-3xl font-bold text-red-700">{stats?.articulos_stock_bajo || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Valor Inventario</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatCurrency(stats?.valor_total_costo)}</p>
                  <p className="text-xs text-green-600">Venta: {formatCurrency(stats?.valor_total_venta)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Movements Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="border-green-200 bg-green-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/inventario/entradas")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Entradas (7 días)</p>
                  <p className="text-3xl font-bold text-green-700">{stats?.entradas_semana || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <ArrowDownToLine className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50/50 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/inventario/salidas")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Salidas (7 días)</p>
                  <p className="text-3xl font-bold text-orange-700">{stats?.salidas_semana || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <ArrowUpFromLine className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock Bajo Alert */}
          {stats?.stock_bajo_list?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Artículos con Stock Bajo
                </CardTitle>
                <CardDescription>Requieren reposición</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {stats.stock_bajo_list.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                        onClick={() => navigate(`/inventario/articulos?search=${item.codigo}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{item.nombre}</p>
                          <p className="text-sm text-slate-500 font-mono">{item.codigo}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="destructive">
                            {item.stock_actual} / {item.stock_minimo}
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">{item.unidad_medida}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Recent Movements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Movimientos Recientes
              </CardTitle>
              <CardDescription>Últimos 7 días</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {stats?.movimientos_recientes?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.movimientos_recientes.map((mov, index) => (
                      <div
                        key={mov.id || index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {mov.tipo === "entrada" ? (
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <ArrowDownToLine className="w-4 h-4 text-green-600" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                              <ArrowUpFromLine className="w-4 h-4 text-orange-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900 text-sm">
                              {mov.articulo?.nombre || "Artículo"}
                            </p>
                            <p className="text-xs text-slate-500">{mov.motivo}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={mov.tipo === "entrada" ? "default" : "secondary"}>
                            {mov.tipo === "entrada" ? "+" : "-"}{mov.cantidad}
                          </Badge>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(mov.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No hay movimientos recientes</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Top Moving Articles */}
          <Card className={stats?.stock_bajo_list?.length === 0 ? "lg:col-span-2" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Artículos más Movidos
              </CardTitle>
              <CardDescription>Mayor actividad en 7 días</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.top_articulos?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_articulos.map((item, index) => (
                    <div
                      key={item._id || index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-600">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{item.articulo?.nombre || "Artículo"}</p>
                          <p className="text-sm text-slate-500 font-mono">{item.articulo?.codigo}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">{item.total_movimientos} mov.</p>
                        <p className="text-xs text-slate-500">{item.total_cantidad} unidades</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No hay datos de movimientos</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={() => navigate("/inventario/articulos")} variant="outline">
            <Box className="w-4 h-4 mr-2" />
            Ver Artículos
          </Button>
          <Button onClick={() => navigate("/inventario/entradas")} className="bg-green-600 hover:bg-green-700">
            <ArrowDownToLine className="w-4 h-4 mr-2" />
            Nueva Entrada
          </Button>
          <Button onClick={() => navigate("/inventario/salidas")} className="bg-orange-600 hover:bg-orange-700">
            <ArrowUpFromLine className="w-4 h-4 mr-2" />
            Nueva Salida
          </Button>
          <Button onClick={() => navigate("/inventario/movimientos")} variant="outline">
            <Clock className="w-4 h-4 mr-2" />
            Ver Historial
          </Button>
        </div>
      </main>
    </div>
  );
}
